import { useEffect, useState } from "react";
import { ArrowRight, ArrowUpRight, Eye, Layers, Menu, Palette, X } from "lucide-react";

import { LogoMark } from "../components/branding";

// Always-dark landing, so the mark is drawn white on black explicitly.
const LandingLogo = ({ size = 30 }) => (
  <div className="flex items-center gap-2.5 select-none">
    <div className="rounded-[10px] bg-white text-black flex items-center justify-center" style={{ width: size, height: size }}>
      <LogoMark size={size * 0.62} />
    </div>
    <span className="text-[19px] font-semibold tracking-tight text-white" style={{ letterSpacing: "-0.02em" }}>
      remifo
    </span>
  </div>
);

const PLATFORMS = ["X", "LinkedIn", "Instagram", "Facebook", "TikTok", "Threads", "YouTube", "Reddit"];

const VALUES = [
  {
    icon: Layers,
    title: "Outsource the work, not the message",
    body: "Hand off the posting if you want. You still own the calendar, the voice, and every post that goes out.",
  },
  {
    icon: Palette,
    title: "Consistent with what you build",
    body: "Posts are grounded in your product updates and solutions, or your own story, so your feed matches reality.",
  },
  {
    icon: Eye,
    title: "Made to be seen",
    body: "Native posts for each platform, from X to Reddit, planned and scheduled on one calendar.",
  },
];

export default function LandingPage({ onGetStarted }) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    document.title = "Remifo, your social media content calendar";
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="min-h-[100dvh] bg-black text-white font-sans">
      {/* Nav */}
      <nav className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${scrolled || menuOpen ? "bg-black/80 backdrop-blur-xl border-b border-white/[0.06]" : "bg-transparent"}`}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-4 sm:py-5">
          <div className="flex items-center justify-between">
            <LandingLogo />
            <div className="hidden md:flex items-center gap-8">
              <button onClick={onGetStarted} className="text-sm font-light text-white/50 hover:text-white transition-colors">Sign in</button>
              <button onClick={onGetStarted} className="inline-flex items-center gap-1.5 bg-white text-black px-4 py-2 rounded-lg text-sm font-medium hover:bg-white/90 transition-colors">
                Get started<ArrowRight size={14} />
              </button>
            </div>
            <button onClick={() => setMenuOpen(!menuOpen)} className="md:hidden p-2 text-white/70" aria-label="Menu">
              {menuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
          {menuOpen ? (
            <div className="md:hidden mt-4 pb-2 space-y-2 border-t border-white/[0.06] pt-4">
              <button onClick={onGetStarted} className="block w-full text-left text-sm font-light text-white/60 py-2">Sign in</button>
              <button onClick={onGetStarted} className="flex items-center justify-center gap-2 w-full bg-white text-black px-5 py-3 rounded-lg text-sm font-medium">
                Get started<ArrowRight size={16} />
              </button>
            </div>
          ) : null}
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-36 sm:pt-48 pb-20 sm:pb-28 px-5 sm:px-8">
        <div className="max-w-4xl mx-auto">
          <p className="text-xs uppercase tracking-[0.2em] text-white/40 font-light mb-7 sm:mb-9">
            For African startups, teams, and creators
          </p>
          <h1 className="text-[2.7rem] leading-[1.03] sm:text-7xl font-light tracking-[-0.02em] mb-7 sm:mb-9">
            Building is easy now.
            <br />
            <span className="text-white/40">Being seen is the hard part.</span>
          </h1>
          <p className="text-lg sm:text-xl text-white/55 font-light leading-relaxed max-w-2xl mb-9 sm:mb-11">
            Remifo is your social media content calendar. African startups, teams, and creators use it to stay seen on every feed, in full control of what goes out and consistent with the product they are building or the personal brand they are growing.
          </p>
          <button onClick={onGetStarted} className="inline-flex items-center gap-2 bg-white text-black px-6 py-3.5 rounded-xl text-base font-medium hover:bg-white/90 transition-all active:scale-[0.98]">
            Get started<ArrowRight size={18} />
          </button>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-5 sm:px-8">
        <div className="border-t border-white/[0.08]" />
      </div>

      {/* One channel, full control */}
      <section className="py-20 sm:py-28 px-5 sm:px-8">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl sm:text-5xl font-light tracking-[-0.02em] leading-[1.05] mb-5">
            One channel.
            <br />
            <span className="text-white/40">Full control.</span>
          </h2>
          <p className="text-base sm:text-lg text-white/50 font-light leading-relaxed max-w-2xl mb-12 sm:mb-16">
            Plenty of teams outsource social media and lose the thread of their own message. Remifo keeps you in control, whether you post yourself or hand it off.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-10 gap-y-10">
            {VALUES.map((v) => {
              const Icon = v.icon;
              return (
                <div key={v.title}>
                  <Icon size={22} className="text-white/70 mb-4" strokeWidth={1.5} />
                  <h3 className="text-lg font-medium mb-2.5">{v.title}</h3>
                  <p className="text-sm text-white/50 font-light leading-relaxed">{v.body}</p>
                </div>
              );
            })}
          </div>

          <div className="mt-14 sm:mt-16 flex flex-wrap gap-x-6 gap-y-2">
            {PLATFORMS.map((p) => (
              <span key={p} className="text-sm font-light text-white/35">{p}</span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 sm:py-32 px-5 sm:px-8 border-t border-white/[0.08]">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-4xl sm:text-6xl font-light tracking-[-0.02em] leading-[1.05] mb-8 sm:mb-10">
            Start being
            <br />
            <span className="text-white/40">seen.</span>
          </h2>
          <button onClick={onGetStarted} className="inline-flex items-center gap-2 bg-white text-black px-7 py-4 rounded-xl text-base font-medium hover:bg-white/90 transition-all active:scale-[0.98]">
            Get started<ArrowRight size={18} />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="px-5 sm:px-8 py-10 sm:py-12 border-t border-white/[0.08]">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
          <div>
            <LandingLogo size={26} />
            <p className="text-sm text-white/40 font-light mt-3">Your social media content calendar.</p>
          </div>
          <div className="flex items-center gap-6">
            <button onClick={onGetStarted} className="inline-flex items-center gap-1.5 text-sm text-white/55 hover:text-white transition-colors font-light">
              Sign in<ArrowUpRight size={15} />
            </button>
            <span className="text-xs text-white/30 font-light">&copy; {new Date().getFullYear()} Remifo</span>
          </div>
        </div>
      </footer>
    </div>
  );
}